import { useMemo } from "react";
import { useAreaAnalysis } from "./useAreaAnalysis";
import { useExportHeatMapReport } from "./useExportHeatMapReport";
import { useHeatMapAreas } from "./useHeatMapAreas";
import { useHeatMapMerchants } from "./useHeatMapMerchants";
import { useHeatMapStatistics } from "./useHeatMapStatistics";
import { useLiveEvents } from "./useLiveEvents";
import { useTargetOpportunities } from "./useTargetOpportunities";
import type { AreaModel } from "../types/Area";
import type { AreaAnalysisModel } from "../types/AreaAnalysis";
import type { HeatmapFilterModel } from "../types/Filter";
import type { ActivityLevelModel, EventDetailModel, LiveEventAreaModel } from "../types/LiveEvent";
import type { MerchantModel } from "../types/Merchant";
import type { QueryState } from "../types/QueryState";
import type { StatisticModel } from "../types/Statistic";
import type { TargetOpportunityModel } from "../types/TargetOppotunity";
import type { DashboardFilterPayload } from "../utils/heat-map.utils";
import { toDashboardFilterPayload, toLiveEventsPayload } from "../utils/heat-map.utils";

export interface HeatMapDashboardQueries {
  statistics: QueryState<StatisticModel>;
  targetOpportunities: QueryState<TargetOpportunityModel[]>;
  areaAnalysis: QueryState<AreaAnalysisModel[]>;
  heatMapAreas: QueryState<AreaModel[]>;
  liveEvents: QueryState<LiveEventAreaModel[]> & {
    activityLevels: ActivityLevelModel[] | null;
    eventDetails: EventDetailModel[] | null;
  };
  merchants: QueryState<MerchantModel[]>;
}

export interface HeatMapDashboardData extends HeatMapDashboardQueries {
  exportReport: (payload: DashboardFilterPayload) => Promise<void>;
  exportLoading: boolean;
  exportError: string | null;
  payload: DashboardFilterPayload;
}

interface UseHeatMapDashboardOptions {
  /**
   * The main dashboard leaves merchants unfetched because
   * CommunityImpactSection owns its unfiltered merchant query.
   * The PDF report keeps the default `true`.
   */
  includeMerchants?: boolean;
}

/**
 * Composes all heat-map dashboard queries for one applied filter.
 * Each query keeps its own data, loading, error, and refetch so every
 * dashboard region can render independent loading/error/retry UI.
 * Pages own filter/UI state and pass the applied filter in;
 * hooks own fetching, loading, error and refetch.
 */
export const useHeatMapDashboard = (
  appliedFilters: HeatmapFilterModel | null,
  options?: UseHeatMapDashboardOptions,
): HeatMapDashboardData => {
  const includeMerchants = options?.includeMerchants ?? true;
  const payload: DashboardFilterPayload = useMemo(
    () => toDashboardFilterPayload(appliedFilters),
    [appliedFilters],
  );
  const liveEventsPayload: DashboardFilterPayload = useMemo(
    () => toLiveEventsPayload(appliedFilters),
    [appliedFilters],
  );

  const statistics = useHeatMapStatistics();
  const targetOpportunities = useTargetOpportunities(payload);
  const areaAnalysis = useAreaAnalysis(payload);
  const heatMapAreas = useHeatMapAreas(payload);
  const liveEvents = useLiveEvents(liveEventsPayload);
  const merchants = useHeatMapMerchants(payload, { enabled: includeMerchants });
  const report = useExportHeatMapReport();

  return {
    statistics,
    targetOpportunities,
    areaAnalysis,
    heatMapAreas,
    liveEvents,
    merchants,
    exportReport: report.exportReport,
    exportLoading: report.loading,
    exportError: report.error,
    payload,
  };
};
